import { useCallback, useMemo } from 'react';
import { useApolloClient, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import {
  ICurrCompanyRequestTypesResults,
  IDefaultRequestTypesResults,
  IDeleteRequestTypeParams,
  ISelectedRequestTypesResults,
  IUpdateRequestTypeProps
} from './request-type.types';
import {
  CREATE_REQUEST_TYPES,
  DELETE_GLOBAL_REQUEST_TYPE,
  DELETE_REQUEST_TYPES,
  GET_COMPANY_REQUEST_TYPES,
  GET_DEFAULT_REQUEST_TYPES,
  GET_SELECTED_REQUEST_TYPES,
  RESTORE_REQUEST_TYPE,
  UPDATE_GLOBAL_REQUEST_TYPE,
  UPDATE_REQUEST_TYPE
} from './request-type.queries';
import { IRequestType, Identifiable } from '../../../lib/types';
import { updateRequestTypeInCache } from './request-type.utils';
import { mergeCacheLists, showError } from '../../../lib/utils';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { GET_REQUEST, LIST_REQUESTS_AS_STAFF } from '../request/request.queries';
import { GET_REQUEST_GROUP, GET_REQUEST_GROUPS } from '../request-group/request-group.queries';
import { GET_CURR_COMPANY } from '../companies/company.queries';

export const useCompanyRequestTypes = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<ICurrCompanyRequestTypesResults>(GET_COMPANY_REQUEST_TYPES, {
    fetchPolicy: 'cache-and-network'
  });

  const requestTypes = useMemo(() => data?.companyRequestTypes, [data?.companyRequestTypes]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    companyRequestTypes: requestTypes
  }), [combinedLoading, error, called, refetch, networkStatus, client, observable, previousData, requestTypes]);
};

export const useDefaultRequestTypes = () => {
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IDefaultRequestTypesResults>(GET_DEFAULT_REQUEST_TYPES, {
    fetchPolicy: 'cache-and-network'
  });

  const defaultRequestTypes = useMemo(() => data?.defaultRequestTypes, [data?.defaultRequestTypes]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    defaultRequestTypes
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, defaultRequestTypes]);
};

export const useSelectedRequestTypes = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<ISelectedRequestTypesResults>(GET_SELECTED_REQUEST_TYPES, {
    fetchPolicy: 'cache-and-network'
  });

  const requestTypes = useMemo(() => data?.requestTypes, [data?.requestTypes]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    requestTypes
  }), [combinedLoading, error, called, refetch, networkStatus, client, observable, previousData, requestTypes]);
};

export const useCreateRequestTypes = () => {
  const [mutation, rest] = useMutation<{ createRequestTypes: IRequestType[] }>(CREATE_REQUEST_TYPES, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.createRequestTypes) {
        // Update curr company request types
        const currRequestTypes = cache.readQuery<ICurrCompanyRequestTypesResults>({
          query: GET_COMPANY_REQUEST_TYPES
        })?.companyRequestTypes;

        const newCurrRequestTypes = [];
        if (currRequestTypes) newCurrRequestTypes.push(...currRequestTypes);
        newCurrRequestTypes.push(...data.createRequestTypes);

        cache.writeQuery({
          query: GET_COMPANY_REQUEST_TYPES,
          data: { companyRequestTypes: newCurrRequestTypes } as ICurrCompanyRequestTypesResults,
          overwrite: true
        });
      }
    }
  });

  const createRequestTypes = useCallback((types: string[]) => mutation({ variables: { types } }), [mutation]);

  return { createRequestTypes, ...rest };
};

export const useDeleteGlobalRequestType = () => {
  const [mutation, rest] = useMutation<{ deleteGlobalRequestType: IRequestType }>(DELETE_GLOBAL_REQUEST_TYPE, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.deleteGlobalRequestType) updateRequestTypeInCache({ cache, type: data.deleteGlobalRequestType });
    },
    onError: (err) => showError('Unable to delete global request type due to other organization dependencies', err)
  });

  const deleteGlobalRequestType = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { deleteGlobalRequestType, ...rest };
};

export const useDeleteRequestTypes = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ deleteRequestTypes: IRequestType[] }, IDeleteRequestTypeParams>(
    DELETE_REQUEST_TYPES,
    {
      context: { serializationKey: 'MUTATION', tracked: true },
      update: (cache, { data }, { variables }) => {
        if (data?.deleteRequestTypes) {
          // Update curr company request types
          const currRequestTypes =
            cache.readQuery<ICurrCompanyRequestTypesResults>({
              query: GET_COMPANY_REQUEST_TYPES
            })?.companyRequestTypes ?? [];

          cache.writeQuery<ICurrCompanyRequestTypesResults>({
            query: GET_COMPANY_REQUEST_TYPES,
            data: {
              companyRequestTypes:
                variables?.permanent && variables.requestTypeIDs
                  ? currRequestTypes.filter((rt) => !variables.requestTypeIDs.find((rti: string) => rti === rt._id))
                  : mergeCacheLists(currRequestTypes, data.deleteRequestTypes)
            },
            overwrite: true
          });

          if (variables?.permanent) {
            // Update selected request types
            const currSelectedRequestTypes =
              cache.readQuery<ISelectedRequestTypesResults>({
                query: GET_SELECTED_REQUEST_TYPES
              })?.requestTypes ?? [];

            cache.writeQuery<ISelectedRequestTypesResults>({
              query: GET_SELECTED_REQUEST_TYPES,
              data: {
                requestTypes: currSelectedRequestTypes.filter(
                  (rt) => !variables.requestTypeIDs.find((rti: string) => rti === rt._id)
                )
              },
              overwrite: true
            });

            client.refetchQueries({
              include: [GET_CURR_COMPANY, GET_REQUEST, LIST_REQUESTS_AS_STAFF, GET_REQUEST_GROUP, GET_REQUEST_GROUPS]
            });
          }
        }
      }
    }
  );

  const deleteRequestTypes = useCallback((variables: IDeleteRequestTypeParams) => mutation({ variables }), [mutation]);

  return { deleteRequestTypes, ...rest };
};

export const useRestoreRequestType = () => {
  const [mutation, rest] = useMutation<{ restoreRequestType: IRequestType }>(RESTORE_REQUEST_TYPE, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.restoreRequestType) updateRequestTypeInCache({ cache, type: data.restoreRequestType });
    }
  });

  const restoreRequestType = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreRequestType, ...rest };
};

export const useUpdateRequestType = () => {
  const [mutation, rest] = useMutation<{ updateRequestType: IRequestType }>(UPDATE_REQUEST_TYPE, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.updateRequestType) updateRequestTypeInCache({ cache, type: data.updateRequestType });
    }
  });

  const updateRequestType = useCallback((variables: IUpdateRequestTypeProps) => mutation({ variables }), [mutation]);

  return { updateRequestType, ...rest };
};

export const useUpdateGlobalRequestType = () => {
  const [mutation, rest] = useMutation<{ updateGlobalRequestType: IRequestType }>(UPDATE_GLOBAL_REQUEST_TYPE, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.updateGlobalRequestType) updateRequestTypeInCache({ cache, type: data.updateGlobalRequestType });
    }
  });

  const updateGlobalRequestType = useCallback(
    (variables: IUpdateRequestTypeProps) => mutation({ variables }),
    [mutation]
  );

  return { updateGlobalRequestType, ...rest };
};
